import React, { useState, useEffect, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Input, Select, Typography, Space } from 'antd';
import {
  InboxOutlined,
  UndoOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  GlobalOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  useAuthStore,
  useChannelStore,
  useMessageStore,
  useServerStore,
  useThreadStore,
} from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import {
  updateChannel,
  deleteChannel,
  archiveChannel,
  moveChannel,
} from '../lib/api';
import { useCan } from '../hooks/usePermissions';
import { channelDisplayName } from '../lib/channelDisplay';
import { routes } from '../lib/routes';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { useToast } from './Toast';

interface Props {
  channelId: string;
  onClose: () => void;
}

export default function ChannelSettingsModal({ channelId, onClose }: Props) {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.currentUser);
  const channels = useChannelStore((s) => s.channels);
  const loadChannels = useChannelStore((s) => s.loadChannels);
  const removeChannel = useChannelStore((s) => s.removeChannel);
  const guildId = useAuthStore((s) => s.guildId);
  const guilds = useServerStore((s) => s.guilds);
  const { showToast } = useToast();
  const channel = channels.find((c) => c.id === channelId);

  const channelName = channel?.name ?? '';
  const isGeneral = channelName === 'general';
  const isChannelOwner = currentUser?.id === channel?.created_by;
  const canDeleteChannel = useCan('channel.delete', channelId);
  const canManageVisibility = useCan('channel.manage_visibility', channelId);
  const canDelete = isChannelOwner && canDeleteChannel && !isGeneral;
  const canArchive = isChannelOwner && canManageVisibility && !isGeneral;
  const isMoveableRoot =
    channel?.type === 'channel' && !channel?.parent_id && !isGeneral && channelName !== 'welcome';
  const ownsSourceGuild = guilds.some(
    (guild) => guild.id === guildId && guild.owner_id === currentUser?.id,
  );
  const moveTargets = guilds.filter(
    (guild) => guild.id !== guildId && guild.owner_id === currentUser?.id,
  );
  const canMove = isChannelOwner && isMoveableRoot && ownsSourceGuild && moveTargets.length > 0;

  const [name, setName] = useState(channelName);
  const [description, setDescription] = useState(channel?.topic ?? '');
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmingMove, setConfirmingMove] = useState(false);
  const [moveTarget, setMoveTarget] = useState<string | undefined>();
  const [moveConfirmation, setMoveConfirmation] = useState('');
  const [moving, setMoving] = useState(false);

  const isArchived = (channel?.archived_at ?? null) != null;
  const visibility = channel?.visibility ?? 'public';

  const handleVisibilitySwitch = async () => {
    const target = visibility === 'public' ? 'private' : 'public';
    setSwitching(true);
    try {
      await updateChannel(channelId, { visibility: target });
      await loadChannels();
      showToast(`Channel is now ${target}`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to switch');
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    setName(channelName);
    setDescription(channel?.topic ?? '');
  }, [channelId, channelName, channel?.topic]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateChannel(channelId, { name, topic: description });
      await loadChannels();
      showToast('Channel updated');
      onClose();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteChannel(channelId);
      removeChannel(channelId);
      onClose();
      showToast('Channel deleted');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Delete failed');
      setDeleting(false);
    }
  };

  const openMoveConfirmation = () => {
    setMoveTarget(moveTargets[0]?.id);
    setMoveConfirmation('');
    setConfirmingMove(true);
  };

  const handleMove = async () => {
    if (!channel || !guildId || !moveTarget || moveConfirmation !== channel.name) return;
    const sourceGuildId = guildId;
    setMoving(true);
    try {
      const moved = await moveChannel(sourceGuildId, channel.id, moveTarget);
      const subtree = moved.source_subtree_channel_ids;
      // HTTP success is authoritative. Clear the old Server snapshot before
      // routing; the shell then fetches a new target-Server snapshot.
      if (useAuthStore.getState().guildId === sourceGuildId) {
        useChannelStore.getState().purgeSubtree(subtree);
        useMessageStore.getState().purgeChannels(subtree);
        useThreadStore.getState().purgeChannels(subtree);
        useTaskStore.getState().purgeChannels(subtree);
      }
      navigate(routes.channel(moved.target_guild_id, moved.channel_id));
      setConfirmingMove(false);
      onClose();
      showToast('Channel moved.');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to move channel');
    } finally {
      setMoving(false);
    }
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => setName(event.target.value);
  const handleDescriptionChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setDescription(event.target.value);

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await archiveChannel(channelId, !isArchived);
      await loadChannels();
      showToast(isArchived ? 'Channel restored' : 'Channel archived');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Archive failed');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <>
      <Modal open title="EDIT CHANNEL" onCancel={onClose} footer={null} width={440}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
          <div>
            <Typography.Text
              strong
              style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Name <span style={{ color: 'var(--danger, #ff4d4f)' }}>*</span>
            </Typography.Text>
            <Input
              value={name}
              onChange={handleNameChange}
              disabled={isGeneral}
              style={{ marginTop: 4 }}
            />
            {isGeneral && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                The #general channel cannot be renamed
              </Typography.Text>
            )}
          </div>

          <div>
            <Typography.Text
              strong
              style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Description <span style={{ color: '#999' }}>(optional)</span>
            </Typography.Text>
            <Input.TextArea
              value={description}
              onChange={handleDescriptionChange}
              rows={3}
              style={{ marginTop: 4 }}
            />
          </div>

          {canManageVisibility && !isGeneral && (
            <div>
              <Typography.Text
                strong
                style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Visibility
              </Typography.Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <Typography.Text>
                  {visibility === 'public' ? (
                    <>
                      <GlobalOutlined /> Public
                    </>
                  ) : (
                    <>
                      <LockOutlined /> Private
                    </>
                  )}
                </Typography.Text>
                <Button size="small" onClick={handleVisibilitySwitch} loading={switching}>
                  Switch to {visibility === 'public' ? 'Private' : 'Public'}
                </Button>
              </div>
            </div>
          )}

          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          </Space>

          {(canArchive || canDelete || canMove) && (
            <>
              <div style={{ borderTop: '1px solid #eee', margin: '8px 0' }} />
              <Space orientation="vertical" style={{ width: '100%' }}>
                {canArchive && (
                  <Button
                    block
                    icon={isArchived ? <UndoOutlined /> : <EyeInvisibleOutlined />}
                    onClick={handleArchive}
                    loading={archiving}
                    style={{ background: '#f5deb3', borderColor: '#f5deb3' }}
                  >
                    {isArchived
                      ? `Restore #${channelDisplayName(channel!)}`
                      : `Hide #${channelDisplayName(channel!)}`}
                  </Button>
                )}
                {canDelete && (
                  <Button
                    block
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setConfirmingDelete(true)}
                  >
                    Delete #{channelDisplayName(channel!)}
                  </Button>
                )}
                {canMove && (
                  <Button
                    danger
                    block
                    icon={<SwapOutlined />}
                    onClick={openMoveConfirmation}
                    data-action="move-channel"
                  >
                    Move #{channelDisplayName(channel)} to another Project
                  </Button>
                )}
              </Space>
            </>
          )}
        </div>
      </Modal>
      {confirmingDelete && (
        <ConfirmDeleteModal
          channelName={channelDisplayName(channel!)}
          onConfirm={handleDelete}
          onCancel={() => setConfirmingDelete(false)}
          loading={deleting}
        />
      )}
      <Modal
        open={confirmingMove}
        title={`Move #${channelDisplayName(channel!)}`}
        onCancel={() => !moving && setConfirmingMove(false)}
        cancelButtonProps={{ disabled: moving }}
        okText="Move channel"
        okButtonProps={{
          danger: true,
          loading: moving,
          disabled: !moveTarget || moveConfirmation !== channelName,
        }}
        onOk={() => void handleMove()}
      >
        <Typography.Paragraph>
          This is a transfer, not a copy. The channel, all threads, messages, tasks, files,
          authorship, assignments, and activity history keep their IDs and remain complete.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          Members, invitations, layouts, and other access derived from the current Project are
          removed. Only your new owner access is created in the target Project.
        </Typography.Paragraph>
        <Typography.Text strong>Target Project</Typography.Text>
        <Select
          aria-label="Target Project"
          style={{ width: '100%', marginTop: 6, marginBottom: 16 }}
          value={moveTarget}
          onChange={setMoveTarget}
          disabled={moving}
          options={moveTargets.map((guild) => ({ value: guild.id, label: guild.name }))}
        />
        <Typography.Text strong>Type {channelName} to confirm</Typography.Text>
        <Input
          aria-label="Channel name confirmation"
          style={{ marginTop: 6 }}
          value={moveConfirmation}
          onChange={(event) => setMoveConfirmation(event.target.value)}
          disabled={moving}
        />
      </Modal>
    </>
  );
}
